import { Document } from '@/model/document2/document';
import { Page } from '@/model/document2/page/page';
import { Design } from '@/model/document2/page/surface/design/design';
import { Layer } from '@/model/document2/page/surface/design/layer/layer';
import { CellData, RowData, ColumnData, InsertionData } from '@/model/document2/insertionData/insertionData';
import { PlainTextLayer } from '@/model/document2/page/surface/design/layer/plainTextLayer';
import { CircleTextLayer } from '@/model/document2/page/surface/design/layer/circleTextLayer';
import { QRcodeLayer } from '@/model/document2/page/surface/design/layer/qrcodeLayer';
import { BarcodeLayer } from '@/model/document2/page/surface/design/layer/barcodeLayer';
import { UuidHelper } from './uuidHelper';
import { NumberLayer } from '@/model/document2/page/surface/design/layer/numberLayer';
import { DebugHelper } from "@/helper/debugHelper";
import { ImageLayer } from "@/model/document2/page/surface/design/layer/imageLayer";
import { UserAgentHelper } from './userAgentHelper';
import { SimpleTextLayer } from '@/model/document2/page/surface/design/layer/simpleTextLayer';

// 差し込みデータ生成用ヘルパークラス
export class InsertionHelper
{
    // 差し込みを行ったデザインを作成
    public static createInsertedDesign(srcDoc : Document, rowIndex : number, isReverse : boolean) : Design
    {
        // 最初のデザインを取得する
        var page = srcDoc.pageList[0];
        var surface = page.getSurface(isReverse);
        var firstDesign = surface.getDesign(0);
        if(firstDesign == null) return new Design();

        // rowData
        var insertionData = srcDoc.insertionData;
        var rowData = insertionData.getRowData(rowIndex);

        if((rowIndex == 0 && !insertionData.isFirstRowAsColumnName) ||
        (rowIndex == 1 && insertionData.isFirstRowAsColumnName))
        {
            // 最初のデザインなので、そのまま返す
            return firstDesign;
        }

        // デザインをクローン
        var newDesign = firstDesign.clone();
        // オリジナルのデザイン
        // このデザインのレイヤーを探して、差し込みデータに差し替えするものは差し替える
        var allLayers = newDesign.getAllLayers(false);
        for(var layerIndex = 0; layerIndex < allLayers.length; ++layerIndex)
        {
            var layer = allLayers[layerIndex];
            if(layer.insertionId != "")
            {
                for(var columnIndex = 0; columnIndex < insertionData.getColumnDataCount(); ++columnIndex)
                {
                    var columnData = insertionData.getColumnData(columnIndex);
                    if(columnData.insertionId == layer.insertionId && columnIndex < rowData.cellDataArray.length)
                    {
                        // 差し込みデータの適用
                        var cellData = rowData.cellDataArray[columnIndex];
                        this.applyInsertion(layer, cellData, columnData);
                    }
                }
            }
        }

        return newDesign;
    }

    // 差し込みを行なったページを作成する
    public static createPage(srcDoc : Document, pageIndex : number) : Page
    {
        // 最初のページを取得する
        var firstPage = srcDoc.pageList[0];

        // 新規ページ作成
        var newPage = srcDoc.createNewPage();

        var insertionData = srcDoc.insertionData;
        // 開始行・終了行
        var startRow = Math.max(insertionData.isFirstRowAsColumnName ? 1 : 0, insertionData.startRow);
        var endRow = Math.min(insertionData.endRow, insertionData.getRowDataCount() - 1);
        // designIndex
        var designIndex = insertionData.offset % newPage.designMaxNumber;
        // ページ
        var tryPageIndex = 0;

        for(var rowIndex = startRow; rowIndex <= endRow; ++rowIndex)
        {
            // rowData
            var rowData = insertionData.getRowData(rowIndex);
            // このrowDataが適用するdesignの数
            var printCount = rowData.isPrint ? rowData.printCount * insertionData.printCount : 0;
            for(var i = 0; i < printCount; ++i)
            {
                if(designIndex >= newPage.designMaxNumber)
                {
                    // デザインの最大数に達したら次のページへ
                    tryPageIndex++;
                    designIndex = 0;
                }
                
                // このページindexが作成したいページindexと一致する場合のみ
                if(tryPageIndex == pageIndex)
                {
                    // 印刷向きに応じてdesingPositionを取得する
                    var pos = this.designIndexToPosition(designIndex, insertionData.insertionDirection, srcDoc);

                    // 片面ずつ適用する
                    for(var surfaceIndex = 0; surfaceIndex < (srcDoc.hasReverseSurface ? 2 : 1); ++surfaceIndex)
                    {
                        // 面
                        var surface = newPage.getSurface(surfaceIndex == 1);
                        // オリジナルデザイン
                        var orgDesign = firstPage.getSurface(surfaceIndex == 1).getDesign(0);
                        if(orgDesign == null) continue;

                        if(pageIndex == 0 && pos == 0 && rowIndex == 0)
                        {
                            // 最初のページかつ最初のデザインの場合はそのまま
                            surface.setDesign(pos, orgDesign);
                        }
                        else
                        {
                            // デザインを作成
                            var newDesign = this.createInsertedDesign(srcDoc, rowIndex, surfaceIndex == 1);
                            surface.setDesign(pos, newDesign);
                        }
                    }
                }
                else if(tryPageIndex > pageIndex)
                {
                    // 終了
                    break;
                }

                // 次のデザインへ
                designIndex++;
            }

            // 終了判定
            if(tryPageIndex > pageIndex) break;
        }

        return newPage;
    }

    // 差し込みデータのページ数を数える
    public static calcPageCount(srcDoc : Document)
    {
        var insertionData = srcDoc.insertionData;
        // 開始行・終了行
        var startRow = Math.max(insertionData.isFirstRowAsColumnName ? 1 : 0, insertionData.startRow);
        var endRow = Math.min(insertionData.endRow, insertionData.getRowDataCount() - 1);
        // designIndex
        var designIndex = insertionData.offset % srcDoc.pageList[0].designMaxNumber;
        // ページ
        var tryPageIndex = 0;

        for(var rowIndex = startRow; rowIndex <= endRow; ++rowIndex)
        {
            // rowData
            var rowData = insertionData.getRowData(rowIndex);
            // このrowDataが適用するdesignの数
            var printCount = rowData.isPrint ? rowData.printCount * insertionData.printCount : 0;
            for(var i = 0; i < printCount; ++i)
            {
                if(designIndex >= srcDoc.pageList[0].designMaxNumber)
                {
                    // デザインの最大数に達したら次のページへ
                    tryPageIndex++;
                    designIndex = 0;
                }

                // 次のデザインへ
                designIndex++;
            }
        }

        return tryPageIndex + 1;
    }

    // 差し込みデータの空白フレーム数を数える
    public static calcBlankFrames(srcDoc : Document)
    {
        var insertionData = srcDoc.insertionData;
        // 開始行・終了行
        var startRow = Math.max(insertionData.isFirstRowAsColumnName ? 1 : 0, insertionData.startRow);
        var endRow = Math.min(insertionData.endRow, insertionData.getRowDataCount() - 1);
        // designIndex
        var designIndex = insertionData.offset % srcDoc.pageList[0].designMaxNumber;
        // ページ
        var tryPageIndex = 0;
        // フレーム数
        var frameCount = 0;

        for(var rowIndex = startRow; rowIndex <= endRow; ++rowIndex)
        {
            // rowData
            var rowData = insertionData.getRowData(rowIndex);
            // このrowDataが適用するdesignの数
            var printCount = rowData.isPrint ? rowData.printCount * insertionData.printCount : 0;
            for(var i = 0; i < printCount; ++i)
            {
                if(designIndex >= srcDoc.pageList[0].designMaxNumber)
                {
                    // デザインの最大数に達したら次のページへ
                    tryPageIndex++;
                    designIndex = 0;
                }

                // 次のデザインへ
                designIndex++;
                // フレーム + 1
                frameCount++;
            }
        }

        // 全フレーム
        var allFrames = (tryPageIndex + 1) * srcDoc.pageList[0].designMaxNumber;

        return allFrames - frameCount;
    }
    

    // ページリストを差し込みデータを元に生成する
    // isPrint : true 印刷用の設定 / false 差し込み編集用の設定
    // * isPrintをfalseにすると、開始行、終了行、開始面、印刷枚数設定が無視される
    public static createPageList(srcDoc : Document, isPrint = true) : Array<Page>
    {
         // 最初のページを取得する
         var firstPage = srcDoc.pageList[0];

         // 新規ページリスト
         var newPageList : Array<Page> = [];
         newPageList.push(srcDoc.createNewPage());
 
         var insertionData = srcDoc.insertionData;
         // 開始行・終了行
         var startRow = Math.max(insertionData.isFirstRowAsColumnName ? 1 : 0, insertionData.startRow);
         var endRow = Math.min(insertionData.endRow, insertionData.getRowDataCount() - 1);
         // designIndex
         var designIndex = insertionData.offset % firstPage.designMaxNumber;
 
         for(var rowIndex = startRow; rowIndex <= endRow; ++rowIndex)
         {
             // rowData
             var rowData = insertionData.getRowData(rowIndex);
             // このrowDataが適用するdesignの数
             var printCount = rowData.isPrint ? rowData.printCount * insertionData.printCount : 0;
             for(var i = 0; i < printCount; ++i)
             {
                 var newPage = newPageList[newPageList.length - 1];
                 if(designIndex >= firstPage.designMaxNumber)
                 {
                     // デザインの最大数に達したら次のページへ
                     newPage = srcDoc.createNewPage();
                     newPageList.push(newPage);
                     designIndex = 0;
                 }
                 
                 {
                     // 印刷向きに応じてdesingPositionを取得する
                     var pos = this.designIndexToPosition(designIndex, insertionData.insertionDirection, srcDoc);
 
                     // 片面ずつ適用する
                     for(var surfaceIndex = 0; surfaceIndex < (srcDoc.hasReverseSurface ? 2 : 1); ++surfaceIndex)
                     {
                         // 面
                         var surface = newPage.getSurface(surfaceIndex == 1);
                         // オリジナルデザイン
                         var orgDesign = firstPage.getSurface(surfaceIndex == 1).getDesign(0);
                         if(orgDesign == null) continue;
 
                         if(newPageList.length == 1 && pos == 0 && rowIndex == 0)
                         {
                             // 最初のページかつ最初のデザインの場合はそのまま
                             surface.setDesign(pos, orgDesign);
                         }
                         else
                         {
                             // デザインを作成
                             var newDesign = this.createInsertedDesign(srcDoc, rowIndex, surfaceIndex == 1);
                             surface.setDesign(pos, newDesign);
                         }
                     }
                 }
                 
 
                 // 次のデザインへ
                 designIndex++;
             }
         }
 
         return newPageList;
    }

    // デザインのインデックスをポジションに変更する
    // direction : 流し込み方向
    public static designIndexToPosition(index : number, direction : number, document : Document)
    {
        if(direction == 1)
        {
            var pos = 0;
            var lastIndex = 0;
            // 縦方向
            var horizontal0 = document.getHorizontalPositionArray(0);
            for(var i = 0; i < horizontal0.length; ++i)
            {
                var vertical = document.getVerticalPositionArray(horizontal0[i]);
                for(var j = 0; j < vertical.length; ++j)
                {
                    if(lastIndex == index)
                    {
                        pos = vertical[j];
                    }
                    lastIndex++;
                }
            }
            return pos;
        }
        else
        {
            // 横方向
            return index;
        }
    }

    // 差し込みを最初のデザインに反映させる
    public static applyInsertionToFirstDesign(document : Document)
    {
        var row = document.insertionData.isFirstRowAsColumnName ? 1 : 0;
        for(var column = 0; column < document.insertionData.getColumnDataCount(); ++column)
        {
            var cellData = document.insertionData.getCellData(row, column);
            var columnData = document.insertionData.getColumnData(column);
            if(cellData != null)
            {
                var layers = InsertionHelper.getOriginalsLayerFromInsertionId(document, columnData.insertionId);
                for(var i = 0; i < layers.length; ++i)
                {
                    var layer = layers[i];
                    InsertionHelper.applyInsertion(layer, cellData, columnData);
                }
            }
        }
    }

    // 差し込みIdを元に、position == 0のデザインからオリジナルレイヤーを取得する
    private static getOriginalsLayerFromInsertionId(document : Document, insertionId : string) : Array<Layer>
    {
        var array : Array<Layer> = new Array();
        if(insertionId != "")
        {
            var firstPage = document.pageList[0];
            var surfaceCount = firstPage.hasReverse ? 2 : 1;
            for(var surfaceIndex = 0; surfaceIndex < surfaceCount; ++surfaceIndex)
            {
                var surface = firstPage.getSurface(surfaceIndex == 1);
                var design = surface.getDesign(0);
                if(design != null)
                {
                    var allLayers = design.getAllLayers(false);
                    for(var i = 0; i < allLayers.length; ++i)
                    {
                        var layer = allLayers[i];
                        if(layer.insertionId == insertionId)
                        {
                            array.push(layer);
                        }
                    }
                }
            }
        }

        return array;
    }

    // レイヤーに差し込みデータを適用する
    public static applyInsertion(layer : Layer, cellData : CellData, columnData : ColumnData)
    {
        if(layer instanceof PlainTextLayer)
        {
            // 通常文字
            // 語頭語尾をセット
            layer.prefix = columnData.prefix;
            layer.suffix = columnData.suffix;
            layer.changeText(cellData.content);
            // 文法を基本文法にリセット
            layer.resetWhileTextInfo();
        }
        else if(layer instanceof CircleTextLayer)
        {
            // 円形文字
            var newText = cellData.content == "" ? "" : columnData.prefix + cellData.content + columnData.suffix;
            layer.changeText(newText);
            // 文法を基本文法にリセット
            layer.resetWhileTextInfo();
        }
        else if(layer instanceof SimpleTextLayer)
        {
            // シンプルテキスト
            // 語頭語尾をセット
            layer.prefix = columnData.prefix;
            layer.suffix = columnData.suffix;
            layer.changeText(cellData.content);
        }
        else if (layer instanceof ImageLayer)
        {
            // 画像
            const url: string = cellData.content.trim();
            if (UserAgentHelper.isElectron()) {
                if (/^(https?:\/\/|file:\/\/)/.test(url)) {
                    layer.insertionContent = url;
                } else {
                    // 特定のスキーム以外はローカルの絶対パスとして扱う
                    layer.insertionContent = `file://${url}`;
                }
                layer.isInsertion = true;
            }
        }
        else if(layer instanceof QRcodeLayer)
        {
            // QR
            var newText = cellData.content == "" ? "" : columnData.prefix + cellData.content + columnData.suffix;
            layer.text = newText;
        }
        else if(layer instanceof BarcodeLayer)
        {
            // Barcode
            var newText = cellData.content == "" ? "" : columnData.prefix + cellData.content + columnData.suffix;
            layer.text = newText;
        }
    }

    // デザインを元に差し込みデータに自動割り当てする
    public static applyDesignToInsertionData(insertionData : InsertionData, design : Design) : InsertionData
    {
        // Rowの個数が0なら追加
        if(insertionData.getRowDataCount() == 0)
        {
            insertionData.addRowData();
        }
        // すべてのレイヤーから差し込み対象なものをセレクトしてRow追加
        var allLayers = design.getAllLayers(false);
        var cell = 0;
        for(var i = 0; i < allLayers.length; ++i)
        {
            var layer = allLayers[i];
            if(this.isInsertableLayer(layer, true))
            {
                // 通常文字, 円形文字, QR, バーコード
                // Idをセットして結びつける
                let id = UuidHelper.getUuid();
                layer.insertionId = id;

                let type = 0;
                let content = "";
                let prefix = "";
                let suffix = "";
                if(layer instanceof PlainTextLayer)
                {
                    type = 0;
                    content = layer.text;
                    prefix = layer.prefix;
                    suffix = layer.suffix;
                }
                else if(layer instanceof CircleTextLayer)
                {
                    type = 0;
                    content = layer.text;
                }
                else if(layer instanceof SimpleTextLayer)
                {
                    type = 0;
                    content = layer.text;
                    prefix = layer.prefix;
                    suffix = layer.suffix;
                }
                else if(layer instanceof ImageLayer)
                {
                    type = 0;
                    content = layer.insertionContent;
                }
                else if(layer instanceof QRcodeLayer)
                {
                    type = 0;
                    content = layer.text;
                }
                else if(layer instanceof BarcodeLayer)
                {
                    type = 0;
                    content = layer.text;
                }
                insertionData.addColumnData(id, prefix, suffix);
                insertionData.setCellData(type, content, 0, cell);
                cell++;
            }

            if(cell >= InsertionData.COLUMN_COUNT_MAX)
            {
                // セルが最大設定可能数に達したら終了
                break;
            }
        }

        return insertionData;
    }

    // レイヤーが差し込み対象か
    // isAuto : 自動差し込み設定かどうか
    public static isInsertableLayer(layer : Layer, isAuto : boolean = false)
    {
        if (!isAuto && UserAgentHelper.isElectron() && (layer.layerType === "rasterImage" || layer.layerType === "vectorImage")) {
            // 自動差し込みでない場合は、Electronでは画像も差し込める
            return true;
        }
        return layer.layerType == "plainText" || layer.layerType == "circleText" || layer.layerType == "simpleText" || layer.layerType == "qrcode" || layer.layerType == "barcode";
    }

    // 列を初期値まで埋める
    public static addColumnDataToInitCount(insertionData : InsertionData) : InsertionData
    {
        var toInit = InsertionData.COLUMN_COUNT_INIT - insertionData.getColumnDataCount();
        if(toInit > 0)
        {
            for(var i = 0; i < toInit; ++i)
            {
                insertionData.addColumnData();
            }
        }

        return insertionData;
    }

    // 連番レイヤーをドキュメントから削除する
    public static removeNumberLayerFromDocument(document : Document)
    {
        for(var pageIndex = 0; pageIndex < document.pageCount; ++pageIndex)
        {
            var page = document.pageList[pageIndex];
            DebugHelper.trace(page);
            for(var surfaceIndex = 0; surfaceIndex < (page.hasReverse ? 2 : 1); ++surfaceIndex)
            {
                var surface = page.getSurface(surfaceIndex == 1);
                for(var pos = 0; pos < page.designMaxNumber; ++pos)
                {
                    var design = surface.getDesign(pos);
                    if(design != null)
                    {
                        this.removeNumberLayerFromDesign(design);
                    }
                }
            }
        }
        // ドキュメントからNumberCoreLayerを削除
        document.numberLayers = [];
    }

    // 連番レイヤーをデザインから削除する
    private static removeNumberLayerFromDesign(design : Design)
    {
        var numberLayers : Array<NumberLayer> = [];
        var allLayers = design.getAllLayers(false);
        for(var i = 0; i < allLayers.length; ++i)
        {
            var layer = allLayers[i];
            if(layer instanceof NumberLayer)
            {
                numberLayers.push(layer);
            }
        }

        for(var i = 0; i < numberLayers.length; ++i)
        {
            var numberLayer = numberLayers[i];
            var parent = design.getParentLayer(numberLayer);
            if(parent != null)
            {
                var index = parent.getLayerIndex(numberLayer);
                if(index >= 0)
                {
                    // レイヤーコンバート
                    var plainTextLayer = this.convertNumberLayerToPlainTextLayer(numberLayer);
                    // レイヤー削除
                    parent.removeLayerByIndex(index);
                    // レイヤー挿入
                    parent.insertLayer(index, plainTextLayer);
                }
            }
        }
    }

    private static convertNumberLayerToPlainTextLayer(layer : NumberLayer) : PlainTextLayer
    {
        var oldIsEmQuad = layer.textInfo.isEmQuad;
        var oldPrefix = layer.prefix;
        var oldSuffix = layer.suffix;

        // テキスト情報をから全角を解除
        layer.textInfo.isEmQuad = false;
        // 語頭・語尾を削除
        layer.prefix = "";
        layer.suffix = "";
        // テキストを取得
        var text = layer.getText();

        layer.textInfo.isEmQuad = oldIsEmQuad;
        layer.prefix = oldPrefix;
        layer.suffix = oldSuffix;

        var plainTextLayer = PlainTextLayer.create(text, layer.x, layer.y, layer.width, layer.height);
        // 文法の書き換え
        plainTextLayer.setWholeTextInfo(layer.textInfo);
        // パラメータのコピー
        plainTextLayer.opacity = layer.opacity;
        plainTextLayer.autoSize = layer.autoSize;
        plainTextLayer.autoNewLine = layer.autoNewLine;
        plainTextLayer.lineSpacing = layer.lineSpacing;
        plainTextLayer.textOrientation = layer.textOrientation;
        plainTextLayer.horizontalAlignment = layer.horizontalAlignment;
        plainTextLayer.verticalAlignment = layer.verticalAlignment;
        plainTextLayer.prefix = layer.prefix;
        plainTextLayer.suffix = layer.suffix;

        plainTextLayer.name = layer.name;

        return plainTextLayer;
    }

    // あるレイヤーが差し込み対象になっているか調べる
    public static isInsertionTargetLayer(layer : Layer, document : Document)
    {
        // 差し込みを使用しない場合は対象ではない
        if(!document.useInsertion) return false;
        // レイヤーが差し込み可能かどうか
        if(!this.isInsertableLayer(layer)) return false;

        for(var column = 0; column < document.insertionData.getColumnDataCount(); ++column)
        {
            var columnData = document.insertionData.getColumnData(column);
            // 差し込みIdが一致した
            if (columnData.insertionId == layer.insertionId) return true;
        }

        return false;
    }
}
